import React, {useEffect} from 'react';
import Modal from 'react-modal';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Pagination } from "swiper";
import 'swiper/scss'
import 'swiper/scss/navigation'
import 'swiper/scss/pagination'
import "./SwiperStyles.scss";
import {IVehicleSearch} from "../../interfaces/VehicleSearch";
import {translateService} from "../../services/TranslateService";
import { Icon } from '../Icon';
import {faPlayCircle} from '@fortawesome/free-solid-svg-icons';
import {cStylesApp} from "../../CustomModalStyles";

interface VehicleImageSwiperProps {
    vehicle: IVehicleSearch;
}

interface ImageSwiperProps {
    vehicle: IVehicleSearch;
    images: string[];
    viewImageLarge: boolean;
    onOpenLarge: (lastSlide: boolean) => void;
    onLeaveLastSlide: () => void;
    isCardResult: boolean;
    width?: number;
    height?: number;
    goToLastSlide?: boolean;
}

SwiperCore.use([Navigation, Pagination]);

function getVideoIdAfterLastSlash(input: string): string {
    // retrieve the video id from the YT url
    const index = input.lastIndexOf('/');
    if (index === -1) {
        return ''; // Return an empty string if '/' is not found
    }
    return input.substring(index + 1);
}

// Top-level on purpose: defined inside VehicleImageSwiper it was a new component type on every
// render, so every card re-render (any DataContext change, e.g. a search tab switch) tore down
// and rebuilt its Swiper and reset the visible photo to the first one (backlog I1).
function ImageSwiper ({ vehicle, images: vehicleImagesArray, viewImageLarge, onOpenLarge, onLeaveLastSlide, ...swiperProps }: ImageSwiperProps) {
    let imageAlt = vehicle.vehicleModelData.manufacturer +
        vehicle.vehicleModelData.name +
        (vehicle.vehicleModelData.dimensions.trunkVolume !== 0 ?
            vehicle.vehicleModelData.dimensions.trunkVolume + ' m³' : '')
    const [activeImageIndex, setActiveImageIndex] = React.useState(!swiperProps.goToLastSlide? 0 : vehicleImagesArray.length);
    const [iframeLoading, setIframeLoading] = React.useState<boolean>(true);

    useEffect(() => {
        if (activeImageIndex !== vehicleImagesArray.length && !iframeLoading) {
            setIframeLoading(true); // show loader again if reopened
        }

        if (swiperProps.goToLastSlide && activeImageIndex !== vehicleImagesArray.length) {
            onLeaveLastSlide();
        }
    }, [activeImageIndex, iframeLoading, swiperProps.goToLastSlide]);

    useEffect(() => {
        if (activeImageIndex === vehicleImagesArray.length) { // Only run the code below if the last slide is active
            fetch('https://googleads.g.doubleclick.net/pagead/viewthroughconversion/962985656/', {
                mode: 'no-cors'}).then(() => {
                // Handle the response if needed, do nothing for now
            }).catch(error => {console.error('Error:', error)});
        }
    }, [activeImageIndex, vehicleImagesArray.length]);

    return(
        <Swiper
            onSwiper={() => {}}
            initialSlide={activeImageIndex}
            spaceBetween={0}
            slidesPerView={1}
            navigation={vehicleImagesArray.length > 1}
            pagination={{ clickable: true }}
            onSlideChange={() => { }}
            onRealIndexChange={(e) => {setActiveImageIndex(e.realIndex)}}
            touchRatio={activeImageIndex !== vehicleImagesArray?.length ? 0 : 1} // removes drag ability
            loop={true}
            className={"VSComponent-swiperContainer"}
            style={{
                width: window.innerWidth > 767 ? (swiperProps.width ?? 243) : '300px',
                height: window.innerWidth > 767 ? (swiperProps.height ?? 162) : '200px'
            }}>
            { vehicleImagesArray.map((image, imageNum) => (
                    <SwiperSlide key={imageNum} className="VSComponent-swiperSlide" >
                        { activeImageIndex === imageNum ?
                            viewImageLarge ?
                                <img
                                    width="600" height="400"
                                    className={"VSComponent-carImage"}
                                    alt={imageAlt}
                                    src={"https://vehicles-cdn.24rent.fi/" + image.replace("large", "mediumplus")}
                                />
                                :
                                <img
                                    width="243" height="162"
                                    className={"VSComponent-carImage"}
                                    alt={imageAlt}
                                    onClick={() => {if (swiperProps.isCardResult) {onOpenLarge(false)}}}
                                    src={"https://vehicles-cdn.24rent.fi/" + image.replace("large", "medium")}
                                />
                            : null
                        }
                    </SwiperSlide>
                ))
            }
            { vehicle.vehicleModelData.videoUrl! && vehicle.vehicleModelData.videoUrl !== "" ?
                <SwiperSlide key={vehicleImagesArray.length} className="VSComponent-swiperSlide"
                             onClick={() => {if(swiperProps.isCardResult) {onOpenLarge(true)}}}>
                    { activeImageIndex === vehicleImagesArray.length ?
                        swiperProps.isCardResult ?
                            <div className='VSComponent-swiperPlayVideoSlide'>
                                {/* A real button: the slide's own onClick opens the large view, and the icon alone
                                    gave keyboard users nothing to press and screen readers nothing to name. */}
                                <button type="button" className="VSComponent-swiperPlayVideoButton"
                                        aria-label={translateService.translate('vehicleSearch.playVideo')}>
                                    <Icon icon={faPlayCircle} size="2xl" color='#fdb022'/>
                                </button>
                            </div> :
                            <>
                                { iframeLoading && (
                                    <svg className="loader-spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
                                        <circle cx="25" cy="25" r="20" />
                                    </svg>
                                )}
                                <iframe
                                    className="VSComponent-swiperVideoSlide"
                                    title='Vehicle video'
                                    sandbox="allow-same-origin allow-scripts allow-presentation"
                                    src={`https://youtube.com/embed/${getVideoIdAfterLastSlash(vehicle.vehicleModelData.videoUrl)}?autoplay=${swiperProps.goToLastSlide ? 1 : 0}&mute=1&disabkekb=1&playsinline=1&iv_load_policy=3`}
                                    onLoad={() => {setIframeLoading(false)} }
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    allowFullScreen
                                    height="auto"
                                    width="auto"
                                    style={{ display: iframeLoading ? 'none' : 'block' }}
                                />
                            </> : null
                    }
                </SwiperSlide> : null
            }
        </Swiper>
    )
}

export function VehicleImageSwiper (props: VehicleImageSwiperProps) {
    let vehicleImagesArray: string[] = [] ;
    const [viewImageLarge, setViewImageLarge] = React.useState<boolean>(false);
    const [viewLastSlide, setViewLastSlide] = React.useState<boolean>(false);

    if( props.vehicle.vehicleImages ) {
        props.vehicle.vehicleImages.forEach((image: any) => {
            let imageNames = image.imageName.replace("jpeg", "webp");
            vehicleImagesArray.push(imageNames);
        })

        if(!vehicleImagesArray.length) {
            // this handles any errors for empty strings from data
            vehicleImagesArray.push(props.vehicle.id + '_medium.jpeg')
        }
    } else {
        vehicleImagesArray.push(props.vehicle.id + '_medium.jpeg')
    }

    const swiperShared = {
        vehicle: props.vehicle,
        images: vehicleImagesArray,
        viewImageLarge,
        onOpenLarge: (lastSlide: boolean) => { setViewImageLarge(true); if (lastSlide) setViewLastSlide(true); },
        onLeaveLastSlide: () => setViewLastSlide(false),
    };

    return (
        <div className={"VSComponent-imageSwiper"}>
            <Modal isOpen={viewImageLarge} style={cStylesApp}>
                <ImageSwiper {...swiperShared} isCardResult={false} width={600} height={400} goToLastSlide={viewLastSlide}/>
                <button onClick={() => {setViewImageLarge(false); if(viewLastSlide){setViewLastSlide(false)}}}
                        className="btn btn-primary btn-outline-primary btn-cta mt-2 w-100 m-auto">
                    {translateService.translate("vehicleSearch1.closeVehicleImageModal")}
                </button>
            </Modal>
            <ImageSwiper {...swiperShared} isCardResult={true}/>
        </div>
    )

}